import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Hash, Trash2, Volume2, X } from 'lucide-react';
import { Button, Input, Modal, Select } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { Channel, ChannelKind } from './channels';

interface ChannelEditorModalProps {
  open: boolean;
  channel: Channel | null;
  channels: Channel[];
  onClose: () => void;
  onSave: (channel: Channel, previousId?: string) => void;
  onDelete: (id: string) => void;
}

const SECTIONS = ['TEXT CHANNELS', 'INFORMATION', 'VOICE CHANNELS', 'RESOURCES', 'COMMUNITY', 'SUPPORT'];
const FIELD = 'border-edge bg-black/25 focus:bg-black/40';

function slugChannelName(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
}

function uniqueId(base: string, channels: Channel[], editingId?: string): string {
  let id = base || 'channel';
  let n = 1;
  const taken = (candidate: string) => channels.some((c) => c.id === candidate && c.id !== editingId);
  while (taken(id)) id = `${base}-${++n}`;
  return id;
}

export function ChannelEditorModal({
  open,
  channel,
  channels,
  onClose,
  onSave,
  onDelete,
}: ChannelEditorModalProps) {
  const editing = Boolean(channel);
  const [kind, setKind] = useState<ChannelKind>('text');
  const [name, setName] = useState('');
  const [topic, setTopic] = useState('');
  const [section, setSection] = useState('TEXT CHANNELS');
  const [readOnly, setReadOnly] = useState(false);

  useEffect(() => {
    if (!open) return;
    setKind(channel?.kind ?? 'text');
    setName(channel?.name ?? '');
    setTopic(channel?.topic ?? '');
    setSection(channel?.section ?? (channel?.kind === 'voice' ? 'VOICE CHANNELS' : 'TEXT CHANNELS'));
    setReadOnly(Boolean(channel?.readOnly));
  }, [channel, open]);

  const cleanName = slugChannelName(name);
  const resolvedId = useMemo(() => uniqueId(cleanName, channels, channel?.id), [channel?.id, channels, cleanName]);
  const canSave = cleanName.length > 0;

  function save() {
    if (!canSave) return;
    onSave(
      {
        id: editing ? channel!.id : resolvedId,
        name: cleanName,
        topic: topic.trim() || undefined,
        kind,
        section: kind === 'voice' && section === 'TEXT CHANNELS' ? 'VOICE CHANNELS' : section,
        readOnly,
      },
      channel?.id,
    );
  }

  return (
    <Modal open={open} onClose={onClose} className="max-w-xl rounded-2xl border border-edge bg-white/[0.04] p-0 backdrop-blur-2xl">
      <div className="relative overflow-hidden rounded-2xl p-7 shadow-[0_30px_90px_rgba(0,0,0,0.55),inset_0_1px_0_rgba(255,255,255,0.08)]">
        <button
          type="button"
          className="absolute right-4 top-4 grid h-8 w-8 place-items-center rounded-full text-lo transition-colors hover:bg-white/[0.06] hover:text-hi"
          onClick={onClose}
          aria-label="Close"
        >
          <X size={16} />
        </button>

        <div className="mb-6">
          <h2 className="text-xl font-black tracking-[-0.4px] text-hi">
            {editing ? 'Edit channel' : 'Create channel'}
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-mid">
            {editing ? 'Update name, topic, or settings.' : 'Channels group conversation. Give it a clear purpose.'}
          </p>
        </div>

        <div className="space-y-4">
          <Field label="Channel type">
            <div className="grid grid-cols-2 gap-2">
              <TypeButton active={kind === 'text'} icon={<Hash size={16} />} label="Text" onClick={() => setKind('text')} />
              <TypeButton active={kind === 'voice'} icon={<Volume2 size={16} />} label="Voice" onClick={() => setKind('voice')} />
            </div>
          </Field>

          <Field label="Name">
            <Input
              autoFocus
              maxLength={32}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. general, intros, wins"
              className={FIELD}
            />
            {cleanName && !editing && (
              <p className="mt-1.5 text-[11px] text-lo">
                Channel id: <span className="text-mid">{resolvedId}</span>
              </p>
            )}
          </Field>

          <Field label="Topic" hint="optional">
            <Input
              maxLength={200}
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="What is this channel for?"
              className={FIELD}
            />
          </Field>

          <Field label="Section">
            <Select value={section} onChange={(e) => setSection(e.target.value)} className={FIELD}>
              {SECTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </Field>

          <button
            type="button"
            className="flex w-full items-center justify-between gap-4 rounded-xl border border-edge bg-white/[0.03] px-4 py-3 text-left transition-colors hover:bg-white/[0.05]"
            onClick={() => setReadOnly((v) => !v)}
          >
            <span>
              <span className="block text-sm font-bold text-hi">Read-only</span>
              <span className="mt-0.5 block text-xs text-mid">Only owners and mods can post. Members can read.</span>
            </span>
            <span
              className={cn(
                'relative h-6 w-11 rounded-pill border transition-colors',
                readOnly ? 'border-white/30 bg-white text-black' : 'border-white/15 bg-white/[0.06]',
              )}
            >
              <span
                className={cn(
                  'absolute top-1 h-4 w-4 rounded-full transition-transform',
                  readOnly ? 'left-6 bg-black' : 'left-1 bg-white/50',
                )}
              />
            </span>
          </button>
        </div>

        <div className="mt-7 flex items-center gap-2 border-t border-edge pt-4">
          {editing && (
            <Button
              type="button"
              variant="ghost"
              className="mr-auto text-danger hover:bg-danger/[0.08] hover:text-danger"
              onClick={() => channel && onDelete(channel.id)}
            >
              <Trash2 size={15} /> Delete
            </Button>
          )}
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!canSave}
            className="bg-white text-black shadow-[0_10px_24px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.9)] hover:brightness-105"
            onClick={save}
          >
            {editing ? 'Save changes' : 'Create channel'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <label className="text-[11px] font-bold uppercase tracking-[0.5px] text-lo">{label}</label>
        {hint && <span className="text-[10px] text-lo">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function TypeButton({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex h-11 items-center justify-center gap-2 rounded-xl border text-sm font-bold transition-all',
        active ? 'border-white/25 bg-white text-black' : 'border-edge bg-white/[0.03] text-mid hover:bg-white/[0.06] hover:text-hi',
      )}
      onClick={onClick}
    >
      {active ? icon : icon}
      {label}
    </button>
  );
}
